import { Injectable } from "@angular/core";
import { GlossaryService } from "@app/api/api/glossary.service";
import { KeywordAndDefinition } from "@app/models/keywordAndDefinition";
import { BehaviorSubject, catchError, ReplaySubject, switchMap, tap } from "rxjs";
import * as _ from 'lodash';
import { StringUtil } from "@app/shared/utils/string.util";
import { KeywordAndDefinitionPostRequestDto } from "@app/api/models/keyword-and-definition-post-request-dto";
import { KeywordAndDefinitionPutRequestDto } from "@app/api/models/keyword-and-definition-put-request-dto";

export interface FilterParam {
    tags: string[]
    search: string
    pageIndex: number
    pageSize: number
}

export interface FilteredResult<T> {
    items: T[]
    totalItems: number
    paginationStart: number
    paginationEnd: number
}

/**
 * Service which manages the state glossary keywords
 * information, so it can be displayed
 */
@Injectable()
export class HomeService {

    /************************************************************************************************************
     * @Observable variables
     ***********************************************************************************************************/
    private _languageCode$ = new ReplaySubject<string>(1);
    private _keywordsAndDefinitionsByLanguage$ = new BehaviorSubject<KeywordAndDefinition[]>([])
    private _tags$ = new BehaviorSubject<string[]>([]);
    private _filteredKeywordsAndDefinitions$ = new ReplaySubject<FilteredResult<KeywordAndDefinition>>(1);

    /************************************************************************************************************
     * @Setters
     ***********************************************************************************************************/
    set languageCode(code: string) { this._languageCode$.next(code); }
    set keywordsAndDefinitionsByLanguage(keywordsAndDefinitions: KeywordAndDefinition[]) { this._keywordsAndDefinitionsByLanguage$.next(keywordsAndDefinitions); }
    set filteredKeywordsAndDefinitions(filteredKeywordsAndDefinitions: FilteredResult<KeywordAndDefinition>) { this._filteredKeywordsAndDefinitions$.next(filteredKeywordsAndDefinitions); }
    set tags(tags: string[]) { this._tags$.next(tags); }
    set filter(filter: FilterParam) { this._filterKeywordsAndDefinitions(filter); }

    /************************************************************************************************************
     * @Getters
     ***********************************************************************************************************/
    get languageCode$() { return this._languageCode$.asObservable(); }
    get keywordsAndDefinitionsByLanguage$() { return this._keywordsAndDefinitionsByLanguage$.asObservable(); }
    get filteredKeywordsAndDefinitions$() { return this._filteredKeywordsAndDefinitions$.asObservable(); }
    get tags$() { return this._tags$.asObservable(); }

    /** */
    constructor(private _glossaryService: GlossaryService) {
        this.languageCode$.pipe(            
            switchMap((languageCode: string) => this._glossaryService.fetchKeywordsByLanguageCode("all", languageCode)),
            tap((keywordsAndDefinitionsByLanguage: KeywordAndDefinition[]) => this.keywordsAndDefinitionsByLanguage = keywordsAndDefinitionsByLanguage),
            switchMap(() => this._glossaryService.fetchAllTags()),
            tap(tags => this.tags = tags)
        ).subscribe()
    }

    /** */
    createKeyword(keywordAndDefinitionPostRequestDto: KeywordAndDefinitionPostRequestDto) {
        return this._glossaryService.createKeyword(keywordAndDefinitionPostRequestDto)
    }

    /** */
    updateKeyWord(keywordAndDefinitionPutRequestDto: KeywordAndDefinitionPutRequestDto) {
        return this._glossaryService.updateKeyword(keywordAndDefinitionPutRequestDto)
    }

    /** */
    deleteKeyword(keywordAndDefinitionId: string) {
        return this._glossaryService.deleteKeyword(keywordAndDefinitionId)
    }

    /** */
    private _filterKeywordsAndDefinitions(filter: FilterParam) {

        // deep clone object
        const allKeywordsAndDefinitions: KeywordAndDefinition[] = _.cloneDeep(this._keywordsAndDefinitionsByLanguage$.getValue());

        const filteredKeywordsAndDefinitions = allKeywordsAndDefinitions
        // tags    
        .filter(keywordsAndDefinitions => filter.tags.includes(keywordsAndDefinitions.tag))
        // search for keywords, definitions, or tags
        .filter(keywordsAndDefinitions => {
            const keywordNormalized            = StringUtil.normalizeStringLowerCase(keywordsAndDefinitions.keyword);
            const definitionNormalized         = StringUtil.normalizeStringLowerCase(keywordsAndDefinitions.definition);
            const tagNormalized                = StringUtil.normalizeStringLowerCase(keywordsAndDefinitions.tag);
            const searchValueNormalized        = StringUtil.normalizeStringLowerCase(filter.search);
            return keywordNormalized.includes(searchValueNormalized) 
                || definitionNormalized.includes(searchValueNormalized) 
                || tagNormalized.includes(searchValueNormalized)
        })

        const items = filteredKeywordsAndDefinitions.slice(filter.pageIndex * filter.pageSize, (filter.pageIndex + 1) * filter.pageSize)
        const paginationEnd = (filter.pageIndex + 1) * filter.pageSize;
        /** update filtered questions */
        this.filteredKeywordsAndDefinitions = {
            items: items,
            totalItems: filteredKeywordsAndDefinitions.length,
            paginationStart: items.length > 0 ? (filter.pageIndex * filter.pageSize) + 1 : 0,
            paginationEnd: paginationEnd < filteredKeywordsAndDefinitions.length ? paginationEnd : filteredKeywordsAndDefinitions.length,
        }
    }
}